import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import { FaPaperPlane, FaClock, FaTimes } from "react-icons/fa";
import {
  listWhatsAppWebChannels,
  listWhatsAppWebSegments,
  getWhatsAppWebSegment,
  createWhatsAppWebCampaign,
} from "../../services/api";

/**
 * Modal de disparo rápido a partir do formulário de IA.
 *
 * Recebe a mensagem pronta + URL da imagem do produto e cria 1 campanha
 * por segmento selecionado. Suporta agendamento.
 */
export default function QuickDispatchFromOfferModal({
  show,
  onClose,
  onDispatched,
  message,
  mediaUrl,
  productName,
  productId,
}) {
  const [channels, setChannels] = useState([]);
  const [segments, setSegments] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [previewByCampaign, setPreviewByCampaign] = useState({});
  const [error, setError] = useState(null);
  const [resultMessage, setResultMessage] = useState(null);

  useEffect(() => {
    if (!show) return;
    setError(null);
    setResultMessage(null);
    Promise.all([
      listWhatsAppWebChannels(),
      listWhatsAppWebSegments(),
    ])
      .then(([chRes, sgRes]) => {
        const chs = chRes?.data?.channels || [];
        setChannels(chs);
        const activeFirst = chs.find((c) => c.status === "active");
        if (activeFirst) setChannelId(activeFirst.id);
        setSegments(sgRes?.data?.segments || sgRes?.data || []);
      })
      .catch(() => setError("Falha ao carregar canais/segmentos."));
  }, [show]);

  // Pré-visualização: pra cada segmento selecionado, busca quantos grupos tem.
  // Usa setter funcional pra não precisar de previewByCampaign nas deps
  // (evita loop infinito de re-render).
  useEffect(() => {
    if (!selectedSegmentIds.length) return;
    selectedSegmentIds.forEach((sid) => {
      setPreviewByCampaign((prev) => {
        if (prev[sid]) return prev; // já buscado, ignora
        // dispara fetch fora do setter — só pra evitar duplicação
        getWhatsAppWebSegment(sid)
          .then((res) => {
            const groupsCount =
              res?.data?.groups?.length ??
              res?.data?.groups_count ??
              res?.data?.total_groups ??
              null;
            setPreviewByCampaign((p) => ({
              ...p,
              [sid]: { groupsCount, name: res?.data?.name },
            }));
          })
          .catch(() => {
            setPreviewByCampaign((p) => ({
              ...p,
              [sid]: { groupsCount: null },
            }));
          });
        // marca como "em andamento" pra não disparar de novo
        return { ...prev, [sid]: { groupsCount: null, loading: true } };
      });
    });
  }, [selectedSegmentIds]);

  const totalGrupos = useMemo(() => {
    return Object.values(previewByCampaign).reduce(
      (sum, p) => sum + (p?.groupsCount ?? 0),
      0
    );
  }, [previewByCampaign]);

  function toggleSegment(id) {
    setSelectedSegmentIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  function reset() {
    setSelectedSegmentIds([]);
    setScheduleEnabled(false);
    setScheduleAt("");
    setError(null);
    setResultMessage(null);
  }

  async function handleSubmit() {
    if (!channelId) {
      setError("Selecione um canal WhatsApp.");
      return;
    }
    if (!selectedSegmentIds.length) {
      setError("Selecione ao menos 1 segmento.");
      return;
    }
    if (scheduleEnabled && !scheduleAt) {
      setError("Defina a data/hora do agendamento ou desative.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setResultMessage(null);

    const baseName = productName
      ? `${productName} — ${new Date().toLocaleString("pt-BR")}`
      : `Disparo IA — ${new Date().toLocaleString("pt-BR")}`;

    const results = await Promise.allSettled(
      selectedSegmentIds.map((segmentId) => {
        const segName = previewByCampaign[segmentId]?.name || "";
        return createWhatsAppWebCampaign({
          name: segName ? `${baseName} (${segName})` : baseName,
          message_type: mediaUrl ? "image" : "text",
          message,
          media_path: mediaUrl || null,
          segment_id: segmentId,
          channel_ids: [channelId],
          scheduled_at: scheduleEnabled ? scheduleAt : null,
          product_id: productId || null,
        });
      })
    );

    const success = results.filter((r) => r.status === "fulfilled" && r.value?.success);
    const failed = results.length - success.length;

    setSubmitting(false);

    if (failed > 0) {
      setError(`${failed} de ${results.length} campanha(s) falharam ao criar.`);
    }
    if (success.length > 0) {
      const msg = scheduleEnabled
        ? `${success.length} campanha(s) agendada(s) com sucesso!`
        : `${success.length} campanha(s) criada(s) — disparando em background.`;
      setResultMessage(msg);
      onDispatched?.();
      // fecha automático em 2s se tudo deu certo
      if (failed === 0) {
        setTimeout(() => {
          reset();
          onClose?.();
        }, 2000);
      }
    }
  }

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaPaperPlane className="mr-2" />
          Disparar oferta no WhatsApp
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {resultMessage && <Alert variant="success">{resultMessage}</Alert>}

        <Form.Group>
          <Form.Label>
            <strong>Canal WhatsApp</strong>
          </Form.Label>
          <Form.Control
            as="select"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            <option value="">Selecione um canal...</option>
            {channels.map((c) => (
              <option
                key={c.id}
                value={c.id}
                disabled={c.status !== "active"}
              >
                {c.name} {c.status === "active" ? "✅" : "⚪ inativo"}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group>
          <Form.Label>
            <strong>Segmentos</strong>{" "}
            <small className="text-muted">
              (1 campanha por segmento selecionado)
            </small>
          </Form.Label>
          {segments.length === 0 ? (
            <p className="text-muted">
              Nenhum segmento cadastrado. Crie em "Segmentos" antes de disparar.
            </p>
          ) : (
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #ddd",
                padding: 10,
                borderRadius: 4,
              }}
            >
              {segments.map((s) => {
                const checked = selectedSegmentIds.includes(s.id);
                const preview = previewByCampaign[s.id];
                return (
                  <div key={s.id} className="mb-1">
                    <Form.Check
                      type="checkbox"
                      id={`seg-${s.id}`}
                      checked={checked}
                      onChange={() => toggleSegment(s.id)}
                      label={
                        <span>
                          {s.name}{" "}
                          {checked && preview?.groupsCount != null && (
                            <Badge variant="info" className="ml-1">
                              {preview.groupsCount} grupos
                            </Badge>
                          )}
                        </span>
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Form.Group>

        {selectedSegmentIds.length > 0 && totalGrupos > 0 && (
          <Alert variant="info">
            Vai disparar pra <strong>{totalGrupos} grupos</strong> no total
            ({selectedSegmentIds.length} segmento{selectedSegmentIds.length > 1 ? "s" : ""})
          </Alert>
        )}

        <Form.Group>
          <Form.Check
            type="switch"
            id="schedule-switch"
            label={
              <span>
                <FaClock className="mr-1" /> Agendar para horário específico
              </span>
            }
            checked={scheduleEnabled}
            onChange={(e) => setScheduleEnabled(e.target.checked)}
          />
          {scheduleEnabled && (
            <Form.Control
              type="datetime-local"
              className="mt-2"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          )}
        </Form.Group>

        <Form.Group>
          <Form.Label>
            <strong>Pré-visualização da mensagem</strong>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={message || ""}
            readOnly
            style={{ background: "#f8f9fa" }}
          />
          {mediaUrl && (
            <small className="text-muted">
              📷 Imagem do produto será anexada à mensagem
            </small>
          )}
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          <FaTimes className="mr-1" /> Cancelar
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={submitting || !channelId || !selectedSegmentIds.length}
        >
          {submitting ? (
            <>
              <Spinner size="sm" animation="border" className="mr-2" />
              Criando...
            </>
          ) : scheduleEnabled ? (
            <>
              <FaClock className="mr-1" /> Agendar disparo
            </>
          ) : (
            <>
              <FaPaperPlane className="mr-1" /> Disparar agora
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
